import { useEffect, useRef, useState } from 'react';
import { voicePatch } from '../voice-patch';
import { ApiError, type Ambience, type Api, type BackupVoiceList, type VoiceInfo, type VoiceSetting } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote, SaveBar, Section, useDraft } from '../ui';
import { errorText, useSpecSave } from './AgentSettings';

/** Provider names look like "Sarah - Mature, Reassuring": short name + its own description. */
const splitName = (name: string): { title: string; sub: string | null } => {
  const i = name.indexOf(' - ');
  return i > 0 ? { title: name.slice(0, i), sub: name.slice(i + 3) } : { title: name, sub: null };
};
/** A provider trait in the panel language when the catalogue knows it; otherwise as given, readable. */
const trait = (t: T, group: string, v: string | null): string | null => {
  if (!v) return null;
  const k = `voice.${group}.${v}`;
  return t(k) === k ? v.replaceAll('_', ' ').replace(/^\p{L}/u, (c) => c.toUpperCase()) : t(k);
};

/** Voice tab: the agent's voice (listen to each reading the agent's own line), speed and background sound. */
export const VoicePanel = ({ api, t, agentId, initial, ambience, onSaved }: { api: Api; t: T; agentId: string; initial: VoiceSetting | null; ambience: Ambience; onSaved: () => void }) => {
  // ADR-071: without the agents permission the chosen voice and settings are shown read-only; the catalogue can
  // still be searched, but samples (spoken by the voice engine) and choosing are not offered.
  const canEdit = useCan()(PERM.agents);
  const d = useDraft({ voiceId: initial?.voiceId ?? null, speed: initial?.speed ?? 1, engine: initial?.engine ?? ('standard' as 'standard' | 'self_hosted' | 'ema'), ownVoice: initial?.ownVoice ?? null, backupVoice: initial?.backupVoice ?? null, ambience });
  const [ownVoices, setOwnVoices] = useState<string[]>();
  const [backups, setBackups] = useState<BackupVoiceList>();
  const s = useSpecSave(api, agentId, onSaved, t);
  const v = d.value;
  const [voices, setVoices] = useState<VoiceInfo[]>();
  const [error, setError] = useState<string>();
  const [playing, setPlaying] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'female' | 'male'>('all');
  const [query, setQuery] = useState('');
  const audio = useRef<HTMLAudioElement | null>(null);
  const urls = useRef(new Map<string, string>());

  useEffect(() => void api.ownVoices().then((r) => setOwnVoices(r.voices)).catch(() => setOwnVoices([])), [api]);
  useEffect(() => void api.backupVoices().then(setBackups).catch(() => setBackups({ default: null, voices: [] })), [api]);
  useEffect(() => {
    api.voices().then((r) => setVoices(r.voices)).catch((e: unknown) => setError(e instanceof ApiError ? errorText(t, e) : t('error.generic')));
    const cache = urls.current;
    return () => {
      audio.current?.pause();
      for (const u of cache.values()) URL.revokeObjectURL(u);
    };
  }, [api, t]);

  const stop = () => {
    audio.current?.pause();
    setPlaying(null);
  };
  /** voiceId "own:<name>" = a voice on the self-hosted server (ADR-048). */
  const listen = async (voiceId: string) => {
    if (playing === voiceId) return stop();
    stop();
    const key = `${voiceId}@${v.speed}`;
    try {
      let url = urls.current.get(key);
      if (!url) {
        setLoading(voiceId);
        url = URL.createObjectURL(
          await (voiceId === 'ema:'
            ? api.emaVoiceSample(agentId, v.speed)
            : voiceId.startsWith('backup:')
            ? api.backupVoiceSample(agentId, voiceId.slice(7), v.speed)
            : voiceId.startsWith('own:')
            ? api.ownVoiceSample(agentId, voiceId.slice(4) || undefined, v.speed)
            : api.voiceSample(agentId, voiceId, v.speed)),
        );
        urls.current.set(key, url);
      }
      const a = audio.current ?? new Audio();
      audio.current = a;
      a.src = url;
      a.onended = () => setPlaying(null);
      await a.play();
      setPlaying(voiceId);
    } catch (e) {
      setError(errorText(t, e));
    } finally {
      setLoading(null);
    }
  };

  const q = query.trim().toLowerCase();
  const shown = (voices ?? [])
    .filter((x) => filter === 'all' || x.gender === filter)
    .filter((x) => !q || x.name.toLowerCase().includes(q) || (x.accent ?? '').toLowerCase().includes(q))
    // The chosen voice first, then the business's own (cloned) voices.
    .sort((a, b) => Number(b.voiceId === v.voiceId) - Number(a.voiceId === v.voiceId) || Number(b.category === 'cloned') - Number(a.category === 'cloned'));

  return (
    <div className="settings">
      <ReadOnlyNote t={t} need={PERM.agents} />
      <Section title={t('voice.engine.title')} description={t('voice.engine.hint')}>
        <div className="choice-row">
          {(['standard', 'self_hosted', 'ema'] as const).map((e) => (
            <label key={e} className={`choice ${v.engine === e ? 'on' : ''}`}>
              <input type="radio" name="voice-engine" checked={v.engine === e} disabled={!canEdit} onChange={() => d.set({ ...v, engine: e })} />
              <span>{t(`voice.engine.${e}`)}<small>{t(`voice.engine.${e}.hint`)}</small></span>
            </label>
          ))}
        </div>
        {v.engine !== 'standard' && error && <div className="banner error small">{error}</div>}
        {v.engine === 'ema' && canEdit && (
          <div className="list">
            <div className="list-row">
              <button
                type="button"
                className={`icon-btn ${playing === 'ema:' ? 'on' : ''}`}
                aria-label={playing === 'ema:' ? t('voice.stop') : t('voice.listen')}
                disabled={loading !== null && loading !== 'ema:'}
                onClick={() => void listen('ema:')}
              >
                {loading === 'ema:' ? '…' : playing === 'ema:' ? '■' : '▶'}
              </button>
              <span className="grow">{t('voice.ema.sample')}</span>
            </div>
          </div>
        )}
        {v.engine === 'self_hosted' && (
          <div className="list">
            {(ownVoices && ownVoices.length ? ownVoices : ['']).map((name) => {
              const key = `own:${name}`;
              const chosen = (v.ownVoice ?? '') === name || (!v.ownVoice && ownVoices?.[0] === name);
              return (
                <div key={key} className={`list-row ${canEdit ? 'clickable ' : ''}${chosen ? 'chosen' : ''}`} onClick={canEdit ? () => d.set({ ...v, ownVoice: name || null }) : undefined}>
                  {canEdit && (
                    <button
                      type="button"
                      className={`icon-btn ${playing === key ? 'on' : ''}`}
                      aria-label={playing === key ? t('voice.stop') : t('voice.listen')}
                      disabled={loading !== null && loading !== key}
                      onClick={(e) => {
                        e.stopPropagation();
                        void listen(key);
                      }}
                    >
                      {loading === key ? '…' : playing === key ? '■' : '▶'}
                    </button>
                  )}
                  <span className="grow">{name || t('voice.engine.defaultVoice')}</span>
                  {chosen && <span className="tag ok">{t('voice.chosen')}</span>}
                </div>
              );
            })}
          </div>
        )}
      </Section>
      {v.engine === 'standard' && (
      <Section title={t('voice.title')} description={t('voice.hint')}>
        {error && <div className="banner error small">{error}</div>}
        <div className="voice-toolbar">
          <input className="search" type="search" value={query} placeholder={t('voice.search')} onChange={(e) => setQuery(e.target.value)} />
          {(['all', 'female', 'male'] as const).map((f) => (
            <button key={f} type="button" className={`chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{t(`voice.filter.${f}`)}</button>
          ))}
        </div>
        {!voices && !error && <p className="muted">{t('boot.loading')}</p>}
        {voices && (
          <div className="list voice-scroll">
            {shown.map((x) => {
              const n = splitName(x.name);
              const chosen = v.voiceId === x.voiceId;
              return (
                <div key={x.voiceId} className={`list-row ${canEdit ? 'clickable ' : ''}${chosen ? 'chosen' : ''}`} onClick={canEdit ? () => d.set({ ...v, voiceId: x.voiceId }) : undefined}>
                  {canEdit && (
                    <button
                      type="button"
                      className={`icon-btn ${playing === x.voiceId ? 'on' : ''}`}
                      aria-label={playing === x.voiceId ? t('voice.stop') : t('voice.listen')}
                      title={playing === x.voiceId ? t('voice.stop') : t('voice.listen')}
                      disabled={loading !== null && loading !== x.voiceId}
                      onClick={(e) => {
                        e.stopPropagation();
                        void listen(x.voiceId);
                      }}
                    >
                      {loading === x.voiceId ? '…' : playing === x.voiceId ? '■' : '▶'}
                    </button>
                  )}
                  <div className="grow">
                    <div className="title">{n.title}</div>
                    <div className="traits">
                      {[trait(t, 'gender', x.gender), trait(t, 'age', x.age), trait(t, 'accent', x.accent)].filter(Boolean).map((tr) => <span key={tr} className="trait">{tr}</span>)}
                      {x.category === 'cloned' && <span className="trait gold">{t('voice.own')}</span>}
                      {n.sub && <span className="sub">{n.sub}</span>}
                    </div>
                  </div>
                  {chosen ? <span className="status live">{t('voice.chosen')}</span> : <input type="radio" name="agent-voice" checked={false} readOnly disabled={!canEdit} aria-label={n.title} />}
                </div>
              );
            })}
          </div>
        )}
        {!v.voiceId && <p className="hint">{t('voice.default')}</p>}
      </Section>
      )}
      {backups && backups.voices.length > 0 && (
        <Section title={t('voice.backup.title')} description={t('voice.backup.hint')}>
          <div className="voice-toolbar">
            {(['all', 'female', 'male'] as const).map((f) => (
              <button key={f} type="button" className={`chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{t(`voice.filter.${f}`)}</button>
            ))}
          </div>
          <div className="list">
            {backups.voices
              .filter((x) => filter === 'all' || x.gender === filter)
              .map((x) => {
                const key = `backup:${x.id}`;
                const chosen = (v.backupVoice ?? backups.default) === x.id;
                return (
                  <div key={key} className={`list-row ${canEdit ? 'clickable ' : ''}${chosen ? 'chosen' : ''}`} onClick={canEdit ? () => d.set({ ...v, backupVoice: x.id }) : undefined}>
                    {canEdit && (
                      <button
                        type="button"
                        className={`icon-btn ${playing === key ? 'on' : ''}`}
                        aria-label={playing === key ? t('voice.stop') : t('voice.listen')}
                        title={playing === key ? t('voice.stop') : t('voice.listen')}
                        disabled={loading !== null && loading !== key}
                        onClick={(e) => {
                          e.stopPropagation();
                          void listen(key);
                        }}
                      >
                        {loading === key ? '…' : playing === key ? '■' : '▶'}
                      </button>
                    )}
                    <div className="grow">
                      <div className="title">{x.id.replace(/^\p{L}/u, (c) => c.toUpperCase())}</div>
                      <div className="traits">
                        {[trait(t, 'gender', x.gender), x.id === backups.default ? t('voice.backup.platformDefault') : null].filter(Boolean).map((tr) => <span key={tr} className="trait">{tr}</span>)}
                      </div>
                    </div>
                    {chosen ? <span className="status live">{t('voice.chosen')}</span> : <input type="radio" name="agent-backup-voice" checked={false} readOnly disabled={!canEdit} aria-label={x.id} />}
                  </div>
                );
              })}
          </div>
        </Section>
      )}
      <Section title={t('voice.speed')} description={t('voice.speed.hint')}>
        <label className="slider">
          <input type="range" min={0.8} max={v.engine !== 'standard' ? 1.3 : 1.2} step={0.05} value={v.speed} disabled={!canEdit} onChange={(e) => d.set({ ...v, speed: Number(e.target.value) })} />
          <span className="mono">{v.speed.toFixed(2)}×</span>
        </label>
      </Section>
      <Section title={t('ambience.title')} description={t('ambience.hint')}>
        <div className="choice-row">
          {(['none', 'office'] as const).map((bed) => (
            <label key={bed} className={`choice ${v.ambience.bed === bed ? 'on' : ''}`}>
              <input type="radio" name="ambience-bed" checked={v.ambience.bed === bed} disabled={!canEdit} onChange={() => d.set({ ...v, ambience: { ...v.ambience, bed } })} />
              <span>{t(`ambience.bed.${bed}`)}<small>{t(`ambience.bed.${bed}.hint`)}</small></span>
            </label>
          ))}
        </div>
        {v.ambience.bed !== 'none' && (
          <label className="slider">
            {t('ambience.level')}
            <input type="range" min={0.04} max={0.24} step={0.02} value={v.ambience.level} disabled={!canEdit} onChange={(e) => d.set({ ...v, ambience: { ...v.ambience, level: Number(e.target.value) } })} />
            <span className="mono">{Math.round((v.ambience.level / 0.12) * 100)}%</span>
          </label>
        )}
      </Section>
      <SaveBar
        t={t}
        need={PERM.agents}
        dirty={d.dirty}
        saving={s.saving}
        error={s.error}
        onReset={d.reset}
        onSave={() =>
          void s.save(
            {
              ...voicePatch(v, initial),
              ambience: v.ambience,
            },
            d.commit,
          )
        }
      />
    </div>
  );
};
